// WouldYouRatherQuizThreeOptions.jsx
import React, { useState, useEffect, useReducer } from "react";
import OptionCard from "../OptionCard/OptionCard";
import VoiceoverCard from "../VoiceoverCard/VoiceoverCard";
import { Card, CardBody, CardGroup } from "../card/card";
import QuizNavigation from "../QuizNavigation/QuizNavigation";
import NavigationButtons from "../NavigationButtons/NavigationButtons";
import ProgressOverview from "../ProgressOverview/ProgressOverview";
import useAutoSaveQuiz from "../../hooks/useAutoSave";
import useArrowNavigation from "../../hooks/useArrowNavigation";
import useQuizUpdater from "../../hooks/useQuizUpdater";
import useVoiceGenerator from "../../hooks/useVoiceGenerator";
import quizReducer from "../../reducers/quiz";
import { initMysteryWouldYouRatherReducer } from "../../reducers/initial";

const MysteryWouldYouRatherQuiz = ({
  quizId,
  onCreateQuiz,
  channel,
  backendQuizData,
}) => {
  // Local navigation index.
  const [currentIndex, setCurrentIndex] = useState(0);
  // Flag to indicate that backend data has been loaded.
  const [isLoaded, setIsLoaded] = useState(false);
  // Initialize state using our reducer.
  const [quizStructure, dispatch] = useReducer(
    quizReducer,
    initMysteryWouldYouRatherReducer
  );

  // Use our custom updater hook (which uses dispatch internally).
  const { updateOptionField } = useQuizUpdater(dispatch);
  // Use the voice generator hook with the channel's voiceId.
  const generateVoice = useVoiceGenerator(channel.voiceId);

  // If backend data is provided, load it into state.
  useEffect(() => {
    if (backendQuizData) {
      dispatch({ type: "LOAD_QUIZ", payload: backendQuizData });
      setIsLoaded(true);
    }
  }, [backendQuizData]);

  // Set up arrow key navigation.
  useArrowNavigation(
    () => setCurrentIndex((prev) => Math.max(0, prev - 1)),
    () =>
      setCurrentIndex((prev) =>
        Math.min(quizStructure.items.length, prev + 1)
      ),
    [quizStructure.items.length, isLoaded]
  );

  // Only trigger autosave when data is loaded.
  useAutoSaveQuiz(quizId, isLoaded ? quizStructure : null);

  // Disable the Create Quiz button if required fields are missing.
  const shouldDisableCreateButton = () => {
    if (!quizStructure?.introvoice?.voice) return true;
    return quizStructure.items.some(
      (item) =>
        !item.voice?.voice ||
        !item.option1.selection ||
        !item.option2.selection ||
        !item.option3.selection
    );
  };

  const handleImagesUpdate = (index, optionKey, images) => {
    dispatch({
      type: "UPDATE_IMAGES",
      payload: { index, optionKey, images },
    });
  };

  const handleImageSelect = (index, optionKey, image) => {
    dispatch({
      type: "UPDATE_SELECTION",
      payload: { index, optionKey, image },
    });
  };

  const handleVoiceoverChange = (index, text) => {
    dispatch({
      type: "UPDATE_VOICEOVER",
      payload: { index, text },
    });
  };

  const handleIntroVoiceoverChange = (text) => {
    dispatch({
      type: "UPDATE_INTRO_VOICEOVER",
      payload: { text },
    });
  };

  const handleGenerateItemVoice = async (index) => {
    try {
      const voice = await generateVoice(quizStructure.items[index].voice.text);
      dispatch({
        type: "UPDATE_ITEM_VOICE",
        payload: { index, voice },
      });
      return voice;
    } catch (error) {
      console.error("Error generating item voice:", error);
      throw error;
    }
  };

  const handleGenerateIntroVoice = async () => {
    try {
      const voice = await generateVoice(quizStructure.introvoice.text);
      dispatch({
        type: "UPDATE_INTRO_VOICE",
        payload: { voice },
      });
      return voice;
    } catch (error) {
      console.error("Error generating intro voice:", error);
      throw error;
    }
  };

  const isItemComplete = (item) => {
    return (
      item.voice?.voice &&
      item.option1?.selection &&
      item.option2?.selection &&
      item.option3?.selection
    );
  };

  const handleFinalCreateQuiz = () => {
    // Map through the items and include all three options.
    const finalStructure = quizStructure.items.map((item) => ({
      option1: { text: item.option1.text, image: item.option1.selection || "" },
      option2: { text: item.option2.text, image: item.option2.selection || "" },
      option3: { text: item.option3.text, image: item.option3.selection || "" },
      voice: { text: item.voice.text, voice: item.voice.voice },
    }));
    onCreateQuiz(finalStructure);
  };

  return (
    <>
      <ProgressOverview
        quizStructure={quizStructure}
        currentIndex={currentIndex}
        onNavigate={setCurrentIndex}
        isItemComplete={isItemComplete}
      />

      {/* Intro Voiceover (index 0) */}
      {currentIndex === 0 && (
        <Card>
          <CardBody>
            <NavigationButtons
              onPrevious={() =>
                setCurrentIndex((prev) => Math.max(0, prev - 1))
              }
              onNext={() =>
                setCurrentIndex((prev) =>
                  Math.min(quizStructure.items.length, prev + 1)
                )
              }
              disablePrevious={currentIndex === 0}
              disableNext={currentIndex === quizStructure.items.length}
            />
            <div className="mb-4">
              <VoiceoverCard
                label="Intro Voiceover"
                voiceover={quizStructure.introvoice}
                onTextChange={handleIntroVoiceoverChange}
                onGenerateVoice={handleGenerateIntroVoice}
                shortcutModifier={"alt"}
              />
            </div>
          </CardBody>
        </Card>
      )}

      {/* Quiz item view (index > 0) */}
      {currentIndex > 0 && (
        <Card>
          <CardBody>
            <QuizNavigation
              currentIndex={currentIndex}
              totalItems={quizStructure.items.length}
              onPrevious={() =>
                setCurrentIndex((prev) => Math.max(0, prev - 1))
              }
              onNext={() =>
                setCurrentIndex((prev) =>
                  Math.min(quizStructure.items.length, prev + 1)
                )
              }
            />
            <VoiceoverCard
              label="Voiceover"
              voiceover={quizStructure.items[currentIndex - 1].voice}
              onTextChange={(text) =>
                handleVoiceoverChange(currentIndex - 1, text)
              }
              onGenerateVoice={() => handleGenerateItemVoice(currentIndex - 1)}
            />
            <CardGroup className="mb-4">
              <OptionCard
                option={quizStructure.items[currentIndex - 1].option1}
                onGoogleQueryChange={(value) =>
                    updateOptionField(currentIndex - 1, "option1", "query", value)
                }
                onNameChange={(value) =>
                  updateOptionField(currentIndex - 1, "option1", "text", value)
                }
                onPromptChange={(value) =>
                  updateOptionField(currentIndex - 1, "option1", "prompt", value)
                }
                onImageSelect={(image) =>
                  handleImageSelect(currentIndex - 1, "option1", image)
                }
                onImagesGenerated={(images) =>
                  handleImagesUpdate(currentIndex - 1, "option1", images)
                }
                uniqueId={`option1-${currentIndex - 1}`}
                optionImageAspectRatio="5/4"
                label="Option 1"
                shortcutModifier={"alt"}
              />
              <OptionCard
                option={quizStructure.items[currentIndex - 1].option2}
                onGoogleQueryChange={(value) =>
                    updateOptionField(currentIndex - 1, "option2", "query", value)
                }
                onNameChange={(value) =>
                  updateOptionField(currentIndex - 1, "option2", "text", value)
                }
                onPromptChange={(value) =>
                  updateOptionField(currentIndex - 1, "option2", "prompt", value)
                }
                onImageSelect={(image) =>
                  handleImageSelect(currentIndex - 1, "option2", image)
                }
                onImagesGenerated={(images) =>
                  handleImagesUpdate(currentIndex - 1, "option2", images)
                }
                uniqueId={`option2-${currentIndex - 1}`}
                optionImageAspectRatio="5/4"
                label="Option 2"
                shortcutModifier={"ctrl"}
              />
              <OptionCard
                option={quizStructure.items[currentIndex - 1].option3}
                onGoogleQueryChange={(value) =>
                    updateOptionField(currentIndex - 1, "option3", "query", value)
                }
                onNameChange={(value) =>
                  updateOptionField(currentIndex - 1, "option3", "text", value)
                }
                onPromptChange={(value) =>
                  updateOptionField(currentIndex - 1, "option3", "prompt", value)
                }
                onImageSelect={(image) =>
                  handleImageSelect(currentIndex - 1, "option3", image)
                }
                onImagesGenerated={(images) =>
                  handleImagesUpdate(currentIndex - 1, "option3", images)
                }
                uniqueId={`option3-${currentIndex - 1}`}
                optionImageAspectRatio="5/4"
                label="Option 3"
                shortcutModifier={"shift"}
              />
            </CardGroup>
          </CardBody>
        </Card>
      )}

      <Card>
        <CardBody>
          <button
            className="btn btn-success w-100"
            disabled={shouldDisableCreateButton()}
            onClick={handleFinalCreateQuiz}
          >
            Create Quiz
          </button>
        </CardBody>
      </Card>
    </>
  );
};

export default MysteryWouldYouRatherQuiz;
